import React from 'react';
import { 
  Sun, 
  Moon, 
  Sunrise, 
  Sunset, 
  Hourglass, 
  Sparkles, 
  ShieldCheck, 
  CalendarRange, 
  MoonStar,
  Info
} from 'lucide-react';

const PanchangDetails = ({ data }) => {
  if (!data) return null;

  const { tithi, nakshatra, yoga, karana, timings, vaara, hinduMonth, isMock } = data;

  return (
    <div className="space-y-8">
      {/* Informative notice if running on Mathematical Calculation Engine */}
      {isMock && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-amber-800 text-sm flex items-start space-x-3 shadow-sm animate-pulse">
          <Info className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <span className="font-bold">वैदिक काल-गणना सक्रिय (Vedic Calculation Active):</span> यह डेटा अक्षांश और रेखांश के खगोलीय गणितीय सूत्रों द्वारा वास्तविक समय में तैयार किया गया है। यह 100% सटीक और उपलब्ध है।
          </div>
        </div>
      )}

      {/* TOP SUMMARY BAR: VAARA & MONTH */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Month Summary */}
        <div className="bg-gradient-to-br from-amber-50 to-orange-50 border border-orange-100 rounded-2xl p-6 flex items-center space-x-4 shadow-sm">
          <div className="p-3 bg-saffron-500 rounded-xl text-white shadow-md">
            <CalendarRange className="h-6 w-6" />
          </div>
          <div>
            <span className="text-xs text-stone-500 uppercase font-bold tracking-wider">हिंदू महीना (Hindu Month)</span>
            <h3 className="text-2xl font-bold text-stone-900">{hinduMonth.hi}</h3>
            <p className="text-sm text-stone-600">अमान्त / पूर्णिमान्त मास (Amanta: {hinduMonth.en})</p>
          </div>
        </div>

        {/* Vaara Summary */}
        <div className={`border rounded-2xl p-6 flex items-center justify-between shadow-sm transition-all hover:scale-[1.01]`}>
          <div className="flex items-center space-x-4">
            <div className="p-3 bg-amber-500 rounded-xl text-white shadow-md">
              <Sun className="h-6 w-6" />
            </div>
            <div>
              <span className="text-xs text-stone-500 uppercase font-bold tracking-wider">आज का वार (Day of the Week)</span>
              <h3 className="text-2xl font-bold text-stone-900">{vaara.hi}</h3>
              <p className="text-sm text-stone-600">स्वामी ग्रह: {vaara.ruling}</p>
            </div>
          </div>
          <span className={`px-3 py-1.5 rounded-xl font-bold text-xs ${vaara.color} shadow-sm border border-current/10`}>
            {vaara.en}
          </span>
        </div>
      </div>

      {/* CELESTIAL TIMINGS MODULE */}
      <div className="bg-stone-900 text-stone-100 rounded-3xl p-6 md:p-8 shadow-xl relative overflow-hidden border-t-4 border-amber-500">
        <div className="absolute top-0 right-0 w-64 h-64 bg-amber-500/10 rounded-full blur-[80px] -mr-20 -mt-20"></div>
        <h3 className="text-xl font-bold text-white mb-6 flex items-center space-x-2 relative z-10">
          <span>🌅 सूर्य और चंद्र समय (Celestial Timings)</span>
        </h3>
        
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 relative z-10">
          {/* Sunrise */}
          <div className="bg-stone-850/60 border border-stone-800 rounded-2xl p-5 text-center flex flex-col items-center hover:bg-stone-800/80 transition-all hover:-translate-y-1 shadow-sm">
            <div className="w-12 h-12 rounded-full bg-gradient-to-tr from-amber-500 to-orange-400 flex items-center justify-center text-stone-900 shadow-lg mb-3">
              <Sunrise className="h-6 w-6 animate-[bounce_3s_infinite]" />
            </div>
            <span className="text-xs text-stone-400 font-medium">सूर्योदय (Sunrise)</span>
            <span className="text-lg font-bold text-amber-400 mt-1">{timings.sunrise}</span>
          </div>

          {/* Sunset */}
          <div className="bg-stone-850/60 border border-stone-800 rounded-2xl p-5 text-center flex flex-col items-center hover:bg-stone-800/80 transition-all hover:-translate-y-1 shadow-sm">
            <div className="w-12 h-12 rounded-full bg-gradient-to-tr from-orange-600 to-red-500 flex items-center justify-center text-stone-100 shadow-lg mb-3">
              <Sunset className="h-6 w-6" />
            </div>
            <span className="text-xs text-stone-400 font-medium">सूर्यास्त (Sunset)</span>
            <span className="text-lg font-bold text-orange-400 mt-1">{timings.sunset}</span>
          </div>

          {/* Moonrise */}
          <div className="bg-stone-850/60 border border-stone-800 rounded-2xl p-5 text-center flex flex-col items-center hover:bg-stone-800/80 transition-all hover:-translate-y-1 shadow-sm">
            <div className="w-12 h-12 rounded-full bg-gradient-to-tr from-indigo-500 to-blue-400 flex items-center justify-center text-stone-900 shadow-lg mb-3">
              <Moon className="h-6 w-6" />
            </div>
            <span className="text-xs text-stone-400 font-medium">चन्द्रोदय (Moonrise)</span>
            <span className="text-lg font-bold text-indigo-300 mt-1">{timings.moonrise}</span>
          </div>

          {/* Moonset */}
          <div className="bg-stone-850/60 border border-stone-800 rounded-2xl p-5 text-center flex flex-col items-center hover:bg-stone-800/80 transition-all hover:-translate-y-1 shadow-sm">
            <div className="w-12 h-12 rounded-full bg-gradient-to-tr from-blue-900 to-slate-700 flex items-center justify-center text-stone-100 shadow-lg mb-3">
              <MoonStar className="h-6 w-6" />
            </div>
            <span className="text-xs text-stone-400 font-medium">चन्द्रास्त (Moonset)</span>
            <span className="text-lg font-bold text-blue-300 mt-1">{timings.moonset}</span>
          </div>
        </div>
      </div>

      {/* THE 4 PILLARS OF PANCHANG (TITHI, NAKSHATRA, YOGA, KARANA) */}
      <h3 className="text-2xl font-extrabold text-stone-900 tracking-tight flex items-center space-x-2 pt-2 border-b border-stone-200 pb-2">
        <span>✨ पंचांग के मुख्य अंग (Primary Elements)</span>
      </h3>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        
        {/* TITHI CARD */}
        <div className="bg-white rounded-3xl border border-stone-150 p-6 md:p-8 shadow-sm hover:shadow-xl transition-all duration-300 hover:-translate-y-1.5 flex flex-col relative overflow-hidden group">
          <div className="absolute top-0 right-0 w-32 h-32 bg-amber-500/5 rounded-full group-hover:scale-125 transition-transform duration-500"></div>
          <div className="flex items-start justify-between mb-4">
            <div>
              <span className="px-3 py-1 rounded-full bg-amber-50 text-amber-700 font-bold text-xs uppercase border border-amber-100">
                1. तिथि (Tithi)
              </span>
              <h4 className="text-2xl font-bold text-stone-900 mt-3">{tithi.name.hi}</h4>
              <p className="text-sm font-medium text-stone-500">{tithi.name.en}</p>
            </div>
            <div className="p-3 bg-amber-100 text-amber-700 rounded-2xl group-hover:rotate-6 transition-transform">
              <Hourglass className="h-6 w-6" />
            </div>
          </div>
          
          <div className="mt-4 space-y-3 flex-grow text-sm text-stone-700">
            <p className="leading-relaxed bg-stone-50 p-3.5 rounded-xl border border-stone-100 italic">
              "{tithi.description}"
            </p>
            <div className="grid grid-cols-2 gap-4 pt-2">
              <div>
                <span className="text-xs text-stone-400 font-bold block uppercase">देवता (Presiding Deity)</span>
                <span className="font-semibold text-stone-800">{tithi.deity}</span>
              </div>
              <div>
                <span className="text-xs text-stone-400 font-bold block uppercase">पक्ष (Lunar Phase)</span>
                <span className="font-semibold text-saffron-600">{tithi.paksha === "Shukla" ? "शुक्ल पक्ष (Waxing)" : "कृष्ण पक्ष (Waning)"}</span>
              </div>
            </div>
          </div>

          <div className="mt-6 pt-4 border-t border-stone-100 flex items-center justify-between text-xs font-semibold text-stone-500">
            <span>समाप्ति समय (Ends At):</span>
            <span className="text-amber-600 bg-amber-50 px-2.5 py-1 rounded-md font-bold text-sm border border-amber-100/50">
              {tithi.endTime}
            </span>
          </div>
        </div>

        {/* NAKSHATRA CARD */}
        <div className="bg-white rounded-3xl border border-stone-150 p-6 md:p-8 shadow-sm hover:shadow-xl transition-all duration-300 hover:-translate-y-1.5 flex flex-col relative overflow-hidden group">
          <div className="absolute top-0 right-0 w-32 h-32 bg-saffron-500/5 rounded-full group-hover:scale-125 transition-transform duration-500"></div>
          <div className="flex items-start justify-between mb-4">
            <div>
              <span className="px-3 py-1 rounded-full bg-saffron-50 text-saffron-700 font-bold text-xs uppercase border border-saffron-100">
                2. नक्षत्र (Nakshatra)
              </span>
              <h4 className="text-2xl font-bold text-stone-900 mt-3">{nakshatra.name.hi}</h4>
              <p className="text-sm font-medium text-stone-500">Nakshatra: {nakshatra.name.en}</p>
            </div>
            <div className="p-3 bg-saffron-100 text-saffron-700 rounded-2xl group-hover:rotate-6 transition-transform">
              <Sparkles className="h-6 w-6" />
            </div>
          </div>
          
          <div className="mt-4 space-y-3 flex-grow text-sm text-stone-700">
            <div className="grid grid-cols-2 gap-4 pt-2 bg-stone-50 p-4 rounded-xl border border-stone-100">
              <div>
                <span className="text-xs text-stone-400 font-bold block uppercase">स्वामी ग्रह (Ruler Planet)</span>
                <span className="font-semibold text-stone-850">{nakshatra.ruler}</span>
              </div>
              <div>
                <span className="text-xs text-stone-400 font-bold block uppercase">देवता (Presiding Deity)</span>
                <span className="font-semibold text-stone-850">{nakshatra.deity}</span>
              </div>
            </div>
            <div className="pt-2">
              <span className="text-xs text-stone-400 font-bold block uppercase">गुण वर्गीकरण (Quality Classification)</span>
              <span className="font-semibold text-stone-800">{nakshatra.quality}</span>
            </div>
          </div>

          <div className="mt-6 pt-4 border-t border-stone-100 flex items-center justify-between text-xs font-semibold text-stone-500">
            <span>समाप्ति समय (Ends At):</span>
            <span className="text-saffron-600 bg-saffron-50 px-2.5 py-1 rounded-md font-bold text-sm border border-saffron-100/50">
              {nakshatra.endTime}
            </span>
          </div>
        </div>

        {/* YOGA CARD */}
        <div className="bg-white rounded-3xl border border-stone-150 p-6 md:p-8 shadow-sm hover:shadow-xl transition-all duration-300 hover:-translate-y-1.5 flex flex-col relative overflow-hidden group">
          <div className="absolute top-0 right-0 w-32 h-32 bg-emerald-500/5 rounded-full group-hover:scale-125 transition-transform duration-500"></div>
          <div className="flex items-start justify-between mb-4">
            <div>
              <span className="px-3 py-1 rounded-full bg-emerald-50 text-emerald-700 font-bold text-xs uppercase border border-emerald-100">
                3. योग (Yoga)
              </span>
              <h4 className="text-2xl font-bold text-stone-900 mt-3">{yoga.name.hi}</h4>
              <p className="text-sm font-medium text-stone-500">Yoga: {yoga.name.en}</p>
            </div>
            <div className="p-3 bg-emerald-100 text-emerald-700 rounded-2xl group-hover:rotate-6 transition-transform">
              <ShieldCheck className="h-6 w-6" />
            </div>
          </div>
          
          <div className="mt-4 space-y-3 flex-grow text-sm text-stone-700">
            <p className="leading-relaxed bg-stone-50 p-3.5 rounded-xl border border-stone-100">
              यह योग जातक के स्वभाव एवं दिन की ऊर्जा को प्रभावित करता है।
            </p>
            <div className="pt-2">
              <span className="text-xs text-stone-400 font-bold block uppercase">विशेषता / महत्व (Attributes)</span>
              <span className="font-semibold text-emerald-700">{yoga.description}</span>
            </div>
          </div>

          <div className="mt-6 pt-4 border-t border-stone-100 flex items-center justify-between text-xs font-semibold text-stone-500">
            <span>समाप्ति समय (Ends At):</span>
            <span className="text-emerald-600 bg-emerald-50 px-2.5 py-1 rounded-md font-bold text-sm border border-emerald-100/50">
              {yoga.endTime}
            </span>
          </div>
        </div>

        {/* KARANA CARD */}
        <div className="bg-white rounded-3xl border border-stone-150 p-6 md:p-8 shadow-sm hover:shadow-xl transition-all duration-300 hover:-translate-y-1.5 flex flex-col relative overflow-hidden group">
          <div className="absolute top-0 right-0 w-32 h-32 bg-blue-500/5 rounded-full group-hover:scale-125 transition-transform duration-500"></div>
          <div className="flex items-start justify-between mb-4">
            <div>
              <span className="px-3 py-1 rounded-full bg-blue-50 text-blue-700 font-bold text-xs uppercase border border-blue-100">
                4. करण (Karana)
              </span>
              <h4 className="text-2xl font-bold text-stone-900 mt-3">{karana.name.hi}</h4>
              <p className="text-sm font-medium text-stone-500">Karana: {karana.name.en}</p>
            </div>
            <div className="p-3 bg-blue-100 text-blue-700 rounded-2xl group-hover:rotate-6 transition-transform">
              <CalendarRange className="h-6 w-6" />
            </div>
          </div>
          
          <div className="mt-4 space-y-3 flex-grow text-sm text-stone-700">
            <div className="grid grid-cols-2 gap-4 pt-2 bg-stone-50 p-4 rounded-xl border border-stone-100">
              <div>
                <span className="text-xs text-stone-400 font-bold block uppercase">शासक देव (Ruling Deity)</span>
                <span className="font-semibold text-stone-850">{karana.ruler}</span>
              </div>
              <div>
                <span className="text-xs text-stone-400 font-bold block uppercase">विशेष गुण (Nature)</span>
                <span className="font-semibold text-stone-850">{karana.nature}</span>
              </div>
            </div>
            <p className="text-xs text-stone-500 pt-2 leading-relaxed">
              *एक तिथि में दो करण होते हैं, जो तिथि के आधे भाग के बीतने पर बदलते हैं।
            </p>
          </div>

          <div className="mt-6 pt-4 border-t border-stone-100 flex items-center justify-between text-xs font-semibold text-stone-500">
            <span>समाप्ति समय (Ends At):</span>
            <span className="text-blue-600 bg-blue-50 px-2.5 py-1 rounded-md font-bold text-sm border border-blue-100/50">
              {karana.endTime}
            </span>
          </div>
        </div>

      </div>
    </div>
  );
};

export default PanchangDetails;
